import { api, type MutOpts } from "@/api"
import { queryOptions, useMutation, useQuery } from "@tanstack/react-query"

import { type FetchOptions } from "@/api/types"
import { queryClient } from "@/lib/query-client"

type DictParam = Record<string, unknown>

// ==================== QUERY KEYS ====================
export const patientKeys = {

  // Patients
  all: ["patients"] as const,
  
  list: <TQuery>(query?: TQuery) => [...patientKeys.all, { query }] as const,

  detail: (id: number, query?: DictParam) => [...patientKeys.list(), id,  { query }] as const,
  create: () => [...patientKeys.all, "create"] as const,
  update: () => [...patientKeys.all, "update"] as const,
  delete: () => [...patientKeys.all, "delete"] as const,
  billableDuration: () => [...patientKeys.all, "billable-duration"] as const,


  // Profile 
  navigation: (currentPatientId: number, query?: DictParam) => [...patientKeys.all, currentPatientId, { query }] as const,
  phoneLocation: (id: number) => [...patientKeys.all, "phone-location", id] as const,
  baselines: (id: number) => [...patientKeys.all, "baselines", id] as const,
  longevityScore: (id: number, query?: DictParam) => [...patientKeys.all, "longevity-score",  id,  { query} ] as const,
  generateInsights: (query?: DictParam) => [...patientKeys.all, "generate-insights", { query }] as const,
  generateScores: (query?: DictParam) => [...patientKeys.all, "health-score", { query }] as const,
  compliantDays: (id: number, timezone?: string) => [...patientKeys.all, "compliant-days", id, { timezone }] as const,
  generateStrokeRiskIndicator: ( path: DictParam, query?: DictParam) => [...patientKeys.all, "stroke-indicator", { path }, { query }] as const,
  chfDetector: (id: number, query?: DictParam) => [...patientKeys.all, "chf-detector", id, { query }] as const,

  alerts: (id: number, query?: DictParam) => [...patientKeys.all, "alerts", id, { query }] as const,
  alertChat: (id: number, query?: DictParam) => [...patientKeys.all, "alert-chat", id, {query}] as const,
  acknowledgeAlert: () => [...patientKeys.all, "acknowledge-alert"] as const,

  sleepSessions: (query?: DictParam) => [...patientKeys.all, "sleep-sessions", { query }] as const,

  pathways: (id: number) => [...patientKeys.all, "pathways", id] as const,
  pathwayAnswers: (pathwayId: number, patientId: number) => [...patientKeys.all, "pathway-answers",  pathwayId, patientId ] as const,

  diary: (patientId: number, date: string, timezone: string) => [...patientKeys.all, 'diary',  patientId, date, timezone] as const,

  sms: (id: number, query?: DictParam) => [...patientKeys.all, "sms", id, {query}] as const,
  sendSms: () => [...patientKeys.all, "send-sms"] as const,

  adtEvents: (id: number, query?: DictParam) => [...patientKeys.all, "adt-events", id, {query}] as const,

  treatments: (id: number) => [...patientKeys.all, "treatments", id] as const,

  medications: (id: number) => [...patientKeys.all, "medications", id] as const,

  healthSummaries: (id: number, query?: DictParam) => [...patientKeys.all, "health-summaries", id, {query}] as const,
  acknowledgeHealthSummary: () => [...patientKeys.all, "health-summaries", "acknowledge"] as const,

  apneaScreenings: (id: number) => [...patientKeys.all, "apnea-screenings", id] as const,
  acknowledgeApneaScreening: () => [...patientKeys.all, "apnea-screenings", "acknowledge"] as const,
  apneaAssessment: (patientId: number, screeningId: number) => [...patientKeys.all, "apnea-assessment",  patientId, screeningId] as const,
  apneaSessionMetrics: (patientId: number, screeningId: number) => [...patientKeys.all, "apnea-session-metrics", patientId, screeningId] as const,


  // Notes
  note: () => [...patientKeys.all, "note"] as const,
  noteList: <TQuery, TPath>(path: TPath, query?: TQuery) => [...patientKeys.note(), "list", path, query] as const,
  createNote: () => [...patientKeys.note(), "create"] as const,

  // Health Metrics
  heartRate: (query: DictParam)=> [...patientKeys.all, "heart-rate", {query}] as const,
  spo2: (query: DictParam)=> [...patientKeys.all, "spo2", {query}] as const,
  steps: (query: DictParam)=> [...patientKeys.all, "steps", {query}] as const,
  calories: (query: DictParam)=> [...patientKeys.all, "calories", {query}] as const,
  glucose: (query: DictParam)=> [...patientKeys.all, "glucose", {query}] as const,
  stress: (query: DictParam)=> [...patientKeys.all, "stress", {query}] as const,
  bp: (query: DictParam)=> [...patientKeys.all, "bp", {query}] as const,
  sleep: (query: DictParam)=> [...patientKeys.all, "sleep", {query}] as const,
  weight: (query: DictParam)=> [...patientKeys.all, "weight", {query}] as const,
  hrv: (query: DictParam)=> [...patientKeys.all, "hrv", {query}] as const,

  adtPathways: (patientId: number) => [...patientKeys.all, "adt-pathways", patientId] as const,
  adtPathwayDetail: (id: number) => [...patientKeys.all, "adt-pathway-detail", id] as const,
}

export const patientsQueryOptions = (
  fetchOpts?: FetchOptions<"get", "/patients/">
) => {
  const query = fetchOpts?.params?.query

  return queryOptions({
    queryKey: patientKeys.list({ query }),
    queryFn: () =>
      api.GET("/patients/", { params: { query } }).then(({ data }) => data),
    staleTime: 5 * 60 * 1000, // 5 min
  })
}

export const usePatientsQuery = (
  fetchOpts?: FetchOptions<"get", "/patients/">
) => {
  const query = fetchOpts?.params?.query

  return useQuery({
    queryKey: patientKeys.list(query),
    queryFn: () => {
      return api
        .GET("/patients/", { params: { query } })
        .then(({ data }) => data)
    },
    staleTime: 5 * 60 * 1000, // 5 min
  })
}

export const usePatientNotes = (
  fetchOpts?: FetchOptions<"get", "/patients/{id}/notes/">,
  options?: { enabled?: boolean }
) => {
  const query = fetchOpts?.params?.query
  const id = fetchOpts?.params?.path?.id as number

  return useQuery({
    queryKey: patientKeys.noteList( { id }, query ),
    queryFn: () => { return api.GET("/patients/{id}/notes/", { params: { query: { pageSize: 5, ...query }, path: { id } },}).then(({ data }) => data)},
    ...options,
    enabled: !!id && (options?.enabled ?? true),
  })
}

export const useHeartRateDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/heart-rate/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query
  return useQuery({
    queryKey: patientKeys.heartRate(query),
    queryFn: () => {
      return api
        .GET("/patients/heart-rate/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useSpo2DataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/spo2/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query

  return useQuery({
    queryKey: patientKeys.spo2(query),
    queryFn: () => {
      return api
        .GET("/patients/spo2/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useStepsDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/steps/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query

  return useQuery({
    queryKey: patientKeys.steps(query),
    queryFn: () => {
      return api
        .GET("/patients/steps/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useCaloriesDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/calories/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query

  return useQuery({
    queryKey: patientKeys.calories(query),
    queryFn: () => {
      return api
        .GET("/patients/calories/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useGlucoseDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/glucose/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query
  return useQuery({
    queryKey: patientKeys.glucose(query),
    queryFn: () => {
      return api
        .GET("/patients/glucose/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useStressDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/stress/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query

  return useQuery({
    queryKey: patientKeys.stress(query),
    queryFn: () => {
      return api
        .GET("/patients/stress/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useBloodPressureDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/blood-pressure/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query
  return useQuery({
    queryKey: patientKeys.bp(query),
    queryFn: () => {
      return api
        .GET("/patients/blood-pressure/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useHeartRateVariabilityDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/heart-rate-variability/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query
  return useQuery({
    queryKey: patientKeys.hrv(query),
    queryFn: () => {
      return api
        .GET("/patients/heart-rate-variability/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useWeightDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/weight/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts?.params?.query
  return useQuery({
    queryKey: patientKeys.weight(query),
    queryFn: () => {
      return api
        .GET("/patients/weight/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useSleepDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/sleep/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts?.params?.query
  return useQuery({
    queryKey: patientKeys.sleep(query),
    queryFn: () => {
      return api
        .GET("/patients/sleep/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useSleepSessionsDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/sleep-sessions/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts?.params?.query
  return useQuery({
    queryKey: patientKeys.sleepSessions(query),
    queryFn: () => {
      return api
        .GET("/patients/sleep-sessions/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const patientDetailsQueryOptions = (
  fetchOpts: FetchOptions<"get", "/patients/{id}/">
) => {
  const patientId = fetchOpts.params.path.id
  const query = fetchOpts.params.query

  return queryOptions({
    queryKey: patientKeys.detail(patientId, query ),
    queryFn: () => { return api.GET("/patients/{id}/", {params: {
            path: { id: Number(patientId) },
            query
          }
        })
        .then(({ data }) => data)
    },
    staleTime: 5 * 60 * 1000, // 5 min
  })
}

export const patientInsightsQueryOptions = (
  fetchOpts: FetchOptions<"get", "/patients/ai-report/{id}/">
) => {
  const params = fetchOpts?.params

  return queryOptions({
    queryKey: patientKeys.generateInsights(params?.query),
    queryFn: () => {
      return api
        .GET("/patients/ai-report/{id}/", { params })
        .then(({ data }) => data)
    },
    enabled: false,
  })
}

export const patientHealthScoreQueryOptions = (
  fetchOpts: FetchOptions<"get", "/patients/health-score/{id}/">
) => {
  const params = fetchOpts?.params

  return queryOptions({
    queryKey: patientKeys.generateScores(params?.query),
    queryFn: () => {
      return api
        .GET("/patients/health-score/{id}/", { params })
        .then(({ data }) => data)
    },
    enabled: false,
  })
}

export const usePatientStrokeRiskIndicatorQuery = (
  fetchOpts: FetchOptions<'get', '/patients/stroke-indicator/{id}/'>,
) => {
  const params = fetchOpts?.params;

  return useQuery({
    queryKey: patientKeys.generateStrokeRiskIndicator(params.path, params?.query),
    queryFn: () => {
      return api.GET('/patients/stroke-indicator/{id}/', { params }).then(({ data }) => data);
    },
    enabled: true
  });
};

export const useCreatePatientMutation = (
  mutationOptions?: MutOpts<"post", "/patients/">
) => {
  return useMutation({
    mutationKey: patientKeys.create(),
    mutationFn: fetchOpts => {
      return api.POST("/patients/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: patientKeys.list(),
      })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

export const useUpdatePatientMutation = (
  mutationOptions?: MutOpts<"patch", "/patients/{id}/">
) => {
  return useMutation({
    mutationKey: patientKeys.update(),
    mutationFn: fetchOpts => {
      return api.PATCH("/patients/{id}/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: patientKeys.list(),
        exact: false,
      })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

export const useCreatePatientNoteMutation = (
  mutationOptions?: MutOpts<"post", "/patients/{id}/notes/">
) => {
  return useMutation({
    mutationKey: patientKeys.createNote(),
    mutationFn: fetchOpts => {
      return api.POST("/patients/{id}/notes/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (data, variables, context) => {
      const id = variables.params?.path?.id
      queryClient.invalidateQueries({
        queryKey: patientKeys.noteList({ id }),
      })
      return mutationOptions?.onSuccess?.(data, variables, context)
    },
  })
}

export const useDeletePatientMutation = (
  mutationOptions?: MutOpts<"delete", "/patients/{id}/">
) => {
  return useMutation({
    mutationKey: patientKeys.delete(),
    mutationFn: fetchOpts => {
      return api.DELETE("/patients/{id}/", fetchOpts)
    },

    ...mutationOptions,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: patientKeys.list(),
      })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

export const useCreatePatientBillableTimeMutation = (
  mutationOptions?: MutOpts<"post", "/patients/billable-duration/">
) => {
  return useMutation({
    mutationKey: patientKeys.billableDuration(),
    mutationFn: fetchOpts => {
      return api.POST("/patients/billable-duration/", fetchOpts)
    },
    ...mutationOptions,
  })
}

export const usePatientDiaryQuery = (
  patientId: number,
  date: string,
  timezone: string
) => {
  return useQuery({
    queryKey: patientKeys.diary(patientId, date, timezone),
    queryFn: () => {
      return api
        .GET("/patients/diary/", {
          params: {
            query: {
              patientId: Number(patientId),
              date,
              timezone
            }
          }
        })
        .then(({ data }) => data);
    },
    staleTime: 5 * 60 * 1000, // 5 min
  });
};

export const usePatientInsightsQuery = (
  fetchOpts: FetchOptions<"get", "/patients/ai-report/{id}/">
) => useQuery(patientInsightsQueryOptions(fetchOpts))

type ChfDetectorFetchOpts = {
  params: {
    path: { id: number };
    query?: { timezone?: string; startDate?: string; endDate?: string };
  };
  enabled?: boolean;
};

export const useChfDetectorQuery = (fetchOpts: ChfDetectorFetchOpts) => {
  const params = fetchOpts?.params;
  return useQuery({
    queryKey: patientKeys.chfDetector(params?.path?.id, params?.query),
    queryFn: async () => {
      const { data, error } = await api.GET("/patients/chf-detector/{id}/", {
        params: params as FetchOptions<"get", "/patients/chf-detector/{id}/">["params"],
      });
      if (error) throw error;
      return data;
    },
    enabled: (fetchOpts.enabled ?? false) && !!params?.path?.id,
  });
};

export const usePatientBaselinesQuery = (
  patientId: number,
  page: number = 1,
  pageSize: number = 3
) => {
  return useQuery({
    queryKey: patientKeys.baselines(patientId),
    queryFn: () => {
      return api
        .GET("/patients/{id}/baselines/", {
          params: {
            path: { id: Number(patientId) },
            query: { page, pageSize }
          }
        })
        .then(({ data }) => data)
    },
    staleTime: 10 * 60 * 1000, // 10 min
  })
}


export const useHealthFormOptionsQuery = () => {
  return useQuery({
    queryKey: ["health-form-options"],
    queryFn: () => { return api.GET("/patients/health-form-options/").then(({ data }) => data) }
  })
}

export const usePatientAlertsQuery = (
  fetchOpts: FetchOptions<"get", "/patients/{id}/alerts/">
) => {
  const query = fetchOpts?.params?.query
  const id = fetchOpts?.params.path.id

  return useQuery({
    queryKey: patientKeys.alerts(id, query),
    queryFn: () => {
      return api
        .GET("/patients/{id}/alerts/", {
          params: { query, path: { id } },
        })
        .then(({ data }) => data)
    },
    staleTime: 0, // always fresh
  })
}


export const usePatientHealthSummariesQuery = (
  fetchOpts: FetchOptions<"get", "/patients/{id}/health-summaries/">
) => {
  const query = fetchOpts?.params?.query
  const id = fetchOpts?.params.path.id
  return useQuery({
    queryKey: patientKeys.healthSummaries(id, query),
    queryFn: () => {
      return api
        .GET("/patients/{id}/health-summaries/", {
          params: { query, path: { id } },
        })
        .then(({ data }) => data)
    },
    enabled: !!id,
    staleTime: 10 * 60 * 1000, // 10 min
  })
}

export const useAcknowledgeHealthSummaryMutation = (
  mutationOptions?: MutOpts<"patch", "/patients/{id}/health-summaries/{summary_id}/acknowledge/">
) => {
  return useMutation({
    mutationKey: patientKeys.acknowledgeHealthSummary(),
    mutationFn: (fetchOpts) => {
      return api.PATCH("/patients/{id}/health-summaries/{summary_id}/acknowledge/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (...args) => {
      const patientId = args[1]?.params?.path?.id
      if (patientId) {
        queryClient.invalidateQueries({
          queryKey: [...patientKeys.all, "health-summaries", patientId],
        })
      }
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}



export const usePatientNavigationQuery = (
  fetchOpts: {
    params: {
      currentPatientId: number,
      query?: Record<string, unknown>
    }
  }
) => {
  const { currentPatientId, query } = fetchOpts.params

  return useQuery({
    queryKey: patientKeys.navigation(currentPatientId, query ?? {}),
    queryFn: () => {
      return api
        .GET("/patients/navigation/", {
          params: {
            query: {
              currentPatientId: String(currentPatientId),
              ...query
            }
          }
        })
        .then(({ data }) => data)
    },
    staleTime: 5 * 60 * 1000, // 5 min
  })
}


export const usePatientPhoneLocationQuery = (patientId: number) => {
  return useQuery({
    queryKey: patientKeys.phoneLocation(patientId),
    queryFn: () => {
      return api
        // @ts-expect-error API
        .GET(`/patients/${patientId}/phone-location/`)
        .then(({ data }) => data)
    },
  })
}

export const usePatientLongevityScoreQuery = (
  fetchOpts: FetchOptions<"get", "/patients/{id}/longevity-score/">
) => {
  const query = fetchOpts?.params?.query
  const id = fetchOpts?.params.path.id

  return useQuery({
    queryKey: patientKeys.longevityScore(id, query),
    queryFn: () => {
      return api
        .GET("/patients/{id}/longevity-score/", {
          params: { query, path: { id } },
        })
        .then(({ data }) => data)
    },
    staleTime: 5 * 60 * 1000, // 5 min
  })
}

export const usePatientCompliantDaysQuery = (
  patientId: number
) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  return useQuery({
    queryKey: patientKeys.compliantDays(patientId, timezone),
    queryFn: () =>
      api.GET("/patients/{id}/compliant-days/", {
        params: {
          path: { id: patientId },
          query: { timezone },
        },
      }).then(({ data }) => data),
  })
}

export const usePatientPathwaysQuery = (
  patientId: number
) => {
  return useQuery({
    queryKey: patientKeys.pathways(patientId),
    queryFn: () => {return api.GET("/patients/{id}/sequences/", {
          params: { path: { id: patientId } },
        })
      .then(({ data }) => data)
    },
    staleTime: 10 * 60 * 1000, // 10 min
  })
}

export const usePatientPathwayAnswersQuery = (
  pathwayId: number,
  patientId: number
) => {
  return useQuery({
    queryKey: patientKeys.pathwayAnswers(pathwayId, patientId),
    queryFn: () => {
      return api
        .GET("/pathways/{pathway_id}/patient/{id}", {
          params: { path: { pathwayId, id: patientId } },
        })
        .then(({ data }) => data)
    },
    enabled: !!pathwayId && !!patientId,
    staleTime: 10 * 60 * 1000, // 10 min
  })
}

export const usePatientAlertChatQuery = (
  patientId: number,
  fetchOpts?: { params?: { query?: { page?: number; pageSize?: number } } }
) => {
  const page = fetchOpts?.params?.query?.page
  const pageSize = fetchOpts?.params?.query?.pageSize

  return useQuery({
    queryKey: patientKeys.alertChat(patientId, fetchOpts?.params?.query),
    queryFn: () => {
      return api
        .GET("/chats/patient/{id}/alerts/", {
          params: { query: { page, pageSize }, path: { id: patientId } },
        })
        .then(({ data }) => data)
    },
    enabled: !!patientId,
    staleTime: 0, // always fresh
  })
}

export const useAcknowledgePatientAlertMutation = (
  mutationOptions?: MutOpts<"patch", "/chats/alerts/{id}/">
) => {
  return useMutation({
    mutationKey: patientKeys.acknowledgeAlert(),
    mutationFn: fetchOpts => {
      return api.PATCH("/chats/alerts/{id}/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (data, variables, context) => {
      const id = variables.params?.path?.id
      queryClient.invalidateQueries({queryKey: patientKeys.alertChat(id),exact: false})
      return mutationOptions?.onSuccess?.(data, variables, context)
    },
  })
}

export const usePatientSmsQuery = (
  patientId: number,
  fetchOpts?: { params?: { query?: DictParam } }
) => {
  const query = fetchOpts?.params?.query

  return useQuery({
    queryKey: patientKeys.sms(patientId, query),
    queryFn: () => {
      return api
        .GET("/patients/{id}/sms/", {
          params: { query, path: { id: patientId } },
        })
        .then(({ data }) => data)
    },
    enabled: !!patientId,
    staleTime: 0, // always fresh
  })
}

export const usePatientSmsMutation = (
  mutationOptions?: MutOpts<"post", "/patients/{id}/sms/">
) => {
  return useMutation({
    mutationKey: patientKeys.sendSms(),
    mutationFn: fetchOpts => {
      return api.POST("/patients/{id}/sms/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (data, variables, context) => {
      const id = variables.params.path.id
      queryClient.invalidateQueries({queryKey: patientKeys.sms(id)})
      return mutationOptions?.onSuccess?.(data, variables, context)
    },
  })
}

export const useApneaScreeningsQuery = (patientId: number) => {
  return useQuery({
    queryKey: patientKeys.apneaScreenings(patientId),
    queryFn: () =>
      api
        .GET("/patients/{id}/apnea-screenings/", {
          params: { path: { id: patientId } },
        })
        .then(({ data }) => data),
    enabled: !!patientId,
    staleTime: 10 * 60 * 1000, // 10 min
  })
}

export const useApneaAssessmentQuery = (
  patientId: number,
  screeningId: number,
  enabled = true
) => {
  return useQuery({
    queryKey: patientKeys.apneaAssessment(patientId, screeningId),
    queryFn: () =>
      api.GET("/patients/{id}/apnea-screenings/{screening_id}/assessment/", {
        params: {
          path: {
            id: patientId,
            screeningId: screeningId
          }
        },
      })
        .then(({ data }) => data),
    enabled: !!patientId && !!screeningId && enabled,
    staleTime: 10 * 60 * 1000, // 10 min
  })
}


export const useApneaSessionMetricsQuery = (
  patientId: number,
  screeningId: number,
  enabled = true
) => {
  return useQuery({
    queryKey: patientKeys.apneaSessionMetrics(patientId, screeningId),
    queryFn: () => {
      return api
        .GET("/patients/{id}/apnea-screenings/{screening_id}/session-metrics/", {
          params: {
            path: {
              id: patientId,
              screeningId: screeningId
            }
          },
        })
        .then(({ data }) => data)
    },
    enabled: !!patientId && !!screeningId && enabled,
    staleTime: 10 * 60 * 1000, // 10 min
  })
}



export const usePatientMedicationsQuery = (patientId: number) => {
  return useQuery({
    queryKey: patientKeys.medications(patientId),
    queryFn: () =>
      api
        .GET("/patients/{id}/medications/", {
          params: { path: { id: patientId } },
        })
        .then(({ data }) => data),
    enabled: !!patientId,
    staleTime: 10 * 60 * 1000, // 10 min
  })
}

export const usePatientTreatmentsQuery = (patientId: number) => {
  return useQuery({
    queryKey: patientKeys.treatments(patientId),
    queryFn: () =>
      api
        .GET("/patients/{id}/treatments/", {
          params: { path: { id: patientId } },
        })
        .then(({ data }) => data),
    enabled: !!patientId,
    staleTime: 10 * 60 * 1000, // 10 min
  })
}

export const useAnswerApneaAssessmentQuestionMutation = (
  mutationOptions?: MutOpts<
    "post",
    "/patients/{id}/apnea-screenings/{screening_id}/assessment/answer/"
  >
) => {
  return useMutation({
    mutationKey: ["patients", "apnea-assessment", "answer"],
    mutationFn: (fetchOpts: FetchOptions<
      "post",
      "/patients/{id}/apnea-screenings/{screening_id}/assessment/answer/"
    >) => {
      return api.POST(
        "/patients/{id}/apnea-screenings/{screening_id}/assessment/answer/",
        fetchOpts
      )
    },
    ...mutationOptions,
    onSuccess: (...args) => {
      const patientId = args[1]?.params?.path?.id
      // @ts-expect-error API
      const screeningId = args[1]?.params?.path?.screening_id as number
      if (patientId != null && screeningId != null) {
        queryClient.invalidateQueries({
          queryKey: patientKeys.apneaAssessment(patientId, screeningId),
        })
      }
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

export const usePatientAdtEventsQuery = (
  patientId: number,
  query?: { page?: number; pageSize?: number }
) => {
  return useQuery({
    queryKey: [...patientKeys.adtEvents(patientId), query],
    queryFn: () =>
      api
        .GET("/adt/events/", {
          params: { query: { patient: patientId, ...query } },
        })
        .then(({ data }) => data),
    staleTime: 0, // always fresh
  })
}

export const usePatientAdtPathwaysQuery = (patientId: number) => {
  return useQuery({
    queryKey: patientKeys.adtPathways(patientId),
    queryFn: () =>
      api
        .GET("/adt/patient-pathways/", {
          params: { query: { patient: patientId } },
        })
        .then(({ data }) => data),
    staleTime: 30_000,
  })
}

export const useAdtPathwayDetailQuery = (id: number, enabled = true) => {
  return useQuery({
    queryKey: patientKeys.adtPathwayDetail(id),
    queryFn: () =>
      api
        .GET("/adt/patient-pathways/{id}/", {
          params: { path: { id } },
        })
        .then(({ data }) => data),
    enabled,
  })
}

export const useCreateAdtEventMutation = (
  mutationOptions?: MutOpts<"post", "/adt/events/">
) => {
  return useMutation({
    mutationFn: (fetchOpts: FetchOptions<"post", "/adt/events/">) =>
      api.POST("/adt/events/", fetchOpts),
    ...mutationOptions,
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: patientKeys.all })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

export const useAcknowledgeApneaScreeningMutation = (
  mutationOptions?: MutOpts<"patch", "/patients/{id}/apnea-screenings/{screening_id}/acknowledge/">
) => {
  return useMutation({
    mutationKey: patientKeys.acknowledgeApneaScreening(),
    mutationFn: fetchOpts => {
      return api.PATCH("/patients/{id}/apnea-screenings/{screening_id}/acknowledge/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (data, variables, context) => {
      const id = variables?.params?.path.id
        queryClient.invalidateQueries({
          queryKey: patientKeys.apneaScreenings(id),
        })
  
      return mutationOptions?.onSuccess?.(data, variables, context)
    },
  })
}
